import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div\n  class=\'dropdown-pane data-dropdown {{if @zfLarge \"zf-large\"}}\'\n  id={{@id}}\n  ...attributes\n  {{did-insert this.setup}}\n  {{did-update this.syncNotClose @notClose}}\n  {{will-destroy this.teardown}}\n>\n  <div class=\'zf-wrap {{@customClass}}\'>\n    <div\n      role=\'button\'\n      class=\'zf-close link-button float-right {{if @title \"\" \"show-for-small-only\"}}\'\n      data-test-bb-dropdown-close\n      {{on \'click\' (fn this.close this._elementId)}}\n    >\n      <FaIcon @icon=\'times\' role=\'presentation\' />\n    </div>\n\n    {{#if @title}}\n      <div class=\'zf-header\'>{{@title}}</div>\n    {{/if}}\n\n    <div class=\'zf-body\'>\n      {{yield}}\n    </div>\n  </div>\n</div>");

var _class, _descriptor, _descriptor2;
const CLASS_IS_OPEN = 'is-open';
const CLASS_DROPDOWN_OPEN = 'dropdown-open';

/**
 * ZfDropdown Component
 *
 * A Foundation-style dropdown pane component with programmatic open/close control.
 * Manages body class for overlay styling and provides zfUi interface for external control.
 *
 * Callers that need the zfUi interface should pass @onZfUiReady={{fn (mut this.zfDropdown)}}
 * (or any @tracked setter) to receive the interface after the dropdown mounts.
 *
 * @class BbDropdownComponent
 * @extends Component
 *
 * @arg {boolean} notClose - If true, dropdown won't auto-close
 * @arg {boolean} zfLarge - If true, applies large styling
 * @arg {string} positionClass - Dropdown position (default: 'bottom-start')
 * @arg {string} id - Optional id for the root element
 * @arg {string} title - Optional dropdown title
 * @arg {string} customClass - Optional extra CSS class
 * @arg {Function} onZfUiReady - Callback receiving the zfUi interface once mounted
 */
let BbDropdownComponent = (_class = class BbDropdownComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Reference to the DOM element (cached by did-insert)
     * @type {HTMLElement|null}
     * @private
     */
    _initializerDefineProperty(this, "_element", _descriptor, this);
    /**
     * Stashed element ID for use in the close-button handler
     * @type {string|null}
     * @private
     */
    _initializerDefineProperty(this, "_elementId", _descriptor2, this);
  }
  /**
   * Factory for creating ZFUI interface objects
   * @param {HTMLElement} element
   * @returns {Object} ZFUI interface
   * @private
   */
  _createZfUi(element) {
    return {
      element,
      /**
       * Opens the dropdown
       * @returns {string} 'open'
       */
      open() {
        this.element?.classList.add(CLASS_IS_OPEN);
        document.body?.classList.add(CLASS_DROPDOWN_OPEN);
        return 'open';
      },
      /**
       * Closes the dropdown
       * @param {HTMLElement} [targetElement] - Optional specific element to close
       * @returns {string|boolean} 'close' or false
       */
      close(targetElement) {
        if (targetElement) {
          targetElement.classList?.remove(CLASS_IS_OPEN);
          return false;
        }
        this.element?.classList.remove(CLASS_IS_OPEN);
        document.body?.classList.remove(CLASS_DROPDOWN_OPEN);
        return 'close';
      }
    };
  }

  /**
   * Lifecycle: setup after insert
   */
  setup(element) {
    this._element = element;
    this._elementId = element.id;
    this.syncNotClose();
    scheduleOnce('afterRender', this, this._initializeDropdown);
  }

  /**
   * Keeps the `not-close` class in sync with @notClose imperatively.
   *
   * `is-open` is toggled imperatively by the global body-click handler
   * (application.js), NOT via Glimmer. Binding `not-close` into the Glimmer
   * `class` attribute made Glimmer rewrite the whole attribute whenever
   * @notClose changed, wiping the foreign `is-open` token and visually
   * closing an open pane mid-interaction (e.g. change-status disdetto confirm
   * step). Toggling a single token here never touches the rest of the class.
   */
  syncNotClose() {
    this._element?.classList.toggle('not-close', !!this.args.notClose);
  }

  /**
   * Lifecycle: teardown before destroy — close dropdown and clean up
   */
  teardown() {
    // Close before unmounting to avoid stale body classes
    if (this._element?.classList.contains(CLASS_IS_OPEN)) {
      this._element.classList.remove(CLASS_IS_OPEN);
      document.body?.classList.remove(CLASS_DROPDOWN_OPEN);
    }
    if (typeof this.args.onZfUiReady === 'function') {
      this.args.onZfUiReady(null);
    }
    this._element = null;
    this._elementId = null;
  }

  /**
   * Initializes the dropdown after render and publishes the zfUi interface
   * @private
   */
  _initializeDropdown() {
    const element = this._element;
    if (!element) return;
    const zfUi = this._createZfUi(element);
    if (typeof this.args.onZfUiReady === 'function') {
      this.args.onZfUiReady(zfUi);
    }
  }

  /**
   * Deferred work method (kept for backward compatibility)
   * @deprecated Use _initializeDropdown instead
   */
  deferredWork() {
    this._initializeDropdown();
  }

  /**
   * Closes a dropdown by element ID
   * Uses native DOM for better performance
   * @param {string} elementId - The ID of the dropdown element to close
   */
  close(elementId) {
    if (!elementId) {
      return;
    }
    const element = document.getElementById(elementId);
    element?.classList.remove(CLASS_IS_OPEN);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_element", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_elementId", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "setup", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setup"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "syncNotClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "syncNotClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "teardown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "teardown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "close", [action], Object.getOwnPropertyDescriptor(_class.prototype, "close"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbDropdownComponent);

export { BbDropdownComponent as default };
//# sourceMappingURL=bb-dropdown.js.map
